import { Notifications } from '~/shell/components/Notifications';
import { LanguageSwitcher } from '~/shell/components/LanguageSwitcher';
import { Help } from '~/shell/components/Help';
import { LucideHouse, LucideShoppingCart, LucideTruck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import type { Area } from '~/common/types/area';
import { PgArea, TgArea } from '~/common/types/area';
import type { JSX } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useWebshopMessages } from '~/features/webshopmessages/hooks/useWebshopMessages';
import { MobileShopLocationSelector } from '~/shell/components/MobileShopLocationSelector';
import { Button } from '~/common/components/ui/button';
import { cn } from '~/common/utils/styleUtil';
import { getLinkRel } from '~/common/utils/urlUtil';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

interface AreaInfo {
  langKey: string;
  icon: JSX.Element;
  area: Area;
}

export function ExtendedMobileNav({ onClick }: Readonly<{ onClick?: () => void }>) {
  const { setIsDialogOpen } = useWebshopMessages();
  const { t } = useTranslation();
  const { area, lang } = useBasePath();
  const navigate = useNavigate();

  const areaInfos: AreaInfo[] = useMemo(
    () => [
      {
        langKey: 'Belieferung',
        icon: <LucideTruck />,
        area: TgArea
      },
      {
        langKey: 'Abholung',
        icon: <LucideShoppingCart />,
        area: PgArea
      }
    ],
    []
  );

  const handleAreaChange = useCallback(
    (value: string) => {
      navigate(`/${lang}/${value}`);
    },
    [lang, navigate]
  );

  return (
    <div className={'mt-12 mb-12 bg-neutral-100'}>
      <div className={'flex flex-col'}>
        {areaInfos.map(({ langKey, icon, area: areaKey }) => (
          <div key={areaKey}>
            <TrackNavigationClick label={t(langKey as any)}>
              <Button
                variant="ghost"
                size={'sm'}
                className={cn('justify-start mr-0 px-4 h-12 w-full', areaKey === area && 'text-primary')}
                data-testid={'notifications-button'}
                onClick={() => handleAreaChange(areaKey)}
              >
                {icon}
                <div className={'ml-2 text-base desktop:text-sm '}>{t(langKey as any)}</div>
              </Button>
            </TrackNavigationClick>
            <hr />
          </div>
        ))}
        {area === PgArea && <MobileShopLocationSelector className={'px-4'} />}
        <TrackNavigationClick label={t('Unternehmen')}>
          <a
            href={'https://www.transgourmet.ch'}
            target={'_blank'}
            rel={getLinkRel('https://www.transgourmet.ch')}
            className={'flex items-center gap-2 h-12 px-4 text-base'}
          >
            <LucideHouse />
            {t('Unternehmen')}
          </a>
        </TrackNavigationClick>
        <hr />
        <Notifications onOpen={() => setIsDialogOpen(true)} className={'px-4'} />
        <hr />
        <Help className={'px-4'} isAgh={area === PgArea} />
        <hr />
        <LanguageSwitcher className={'px-4'} onClose={onClick} />
      </div>
    </div>
  );
}
